import React from 'react';
import { Link } from 'react-router-dom';
import { ExternalLink, MapPin, Facebook, ArrowRight } from 'lucide-react';
import { EntIcon, GynecologyIcon, LaparoscopyIcon, UrologyIcon } from '../icons/XionIcons';
import { companyInfo, navLinks, distributor } from '../../data/mock';

const MEDICAL_FIELDS = [
  { name: 'ENT', slug: 'ent', Icon: EntIcon },
  { name: 'Gynaecology', slug: 'gynecology', Icon: GynecologyIcon },
  { name: 'Laparoscopy', slug: 'laparoscopy', Icon: LaparoscopyIcon },
  { name: 'Urology', slug: 'urology', Icon: UrologyIcon },
];

const Footer = () => {
  return (
    <footer className="footer">
      <div className="max-w-7xl mx-auto px-6">
        {/* Distributor Highlight - Most Prominent */}
        {/* // Distributor exclusivity statement is client-provided; confirm final legal wording before launch. */}
        <div className="distributor-badge mb-12 mx-auto w-fit" data-testid="footer-distributor-badge">
          <MapPin size={20} className="text-[#F08A24]" />
          <div>
            <p className="terminal-label mb-1">Exclusive Philippine Distributor</p>
            <p className="font-semibold text-white">{distributor.name}</p>
            <a 
              href={distributor.website}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-distributor-badge-link"
              className="text-[#F08A24] hover:text-white transition-colors flex items-center gap-1 text-sm mt-1"
            >
              {distributor.displayUrl}
              <ExternalLink size={12} />
            </a>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-12">
          {/* Brand Column */}
          <div>
            <img 
              src={companyInfo.logoUrl} 
              alt="XION Medical" 
              className="h-8 w-auto mb-4"
            />
            <p className="text-white/60 text-sm leading-relaxed mb-4">
              {companyInfo.tagline}<br />
              Endoscopy systems since {companyInfo.founded}.
            </p>
          </div>

          {/* Navigation */}
          <div>
            <h4 className="terminal-label text-[#00FFD1] mb-4">// Navigation</h4>
            <nav className="flex flex-col gap-2">
              {navLinks.slice(0, 4).map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  className="text-white/60 hover:text-[#00FFD1] transition-colors text-sm"
                >
                  {link.name}
                </Link>
              ))}
            </nav>
          </div>

          {/* More Links */}
          <div>
            <h4 className="terminal-label text-[#00FFD1] mb-4">// Resources</h4>
            <nav className="flex flex-col gap-2">
              {navLinks.slice(4).map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  className="text-white/60 hover:text-[#00FFD1] transition-colors text-sm"
                >
                  {link.name}
                </Link>
              ))}
              <a 
                href="https://www.xion-medical.com/en/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-white/60 hover:text-[#00FFD1] transition-colors text-sm flex items-center gap-1"
              >
                XION Global
                <ExternalLink size={12} />
              </a>
            </nav>
          </div>

          {/* Contact PH */}
          <div>
            <h4 className="terminal-label text-[#00FFD1] mb-4">// Contact (Philippines)</h4>
            <div className="flex flex-col gap-3 text-sm">
              {/* // Distributor exclusivity statement is client-provided; confirm final legal wording before launch. */}
              <p className="text-white/60 text-xs">
                Exclusive distributor in the Philippines:
              </p>
              <p className="text-white font-medium">
                {distributor.name}
              </p>
              <a 
                href={distributor.website}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-distributor-url-link"
                className="text-[#F08A24] hover:text-white transition-colors flex items-center gap-2"
              >
                <ExternalLink size={14} />
                {distributor.displayUrl}
              </a>
              <div className="flex flex-col gap-2 mt-2">
                <a
                  href={distributor.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="footer-visit-fms-btn"
                  className="btn-distributor text-xs py-2"
                >
                  Visit FMSInc. Website
                  <ExternalLink size={12} />
                </a>
                <Link
                  to="/contact-ph"
                  data-testid="footer-inquire-btn"
                  className="btn-navy text-xs py-2"
                >
                  Inquire Now
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* Medical Fields — highlighted range of XION solutions */}
        <div
          className="relative rounded-xl border border-[#00FFD1]/30 bg-gradient-to-br from-[#00FFD1]/[0.07] via-[#00FFD1]/[0.02] to-transparent p-6 md:p-7 mb-10 shadow-[0_0_44px_-14px_rgba(0,255,209,0.5)]"
          data-testid="footer-medical-fields"
        >
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-5">
            <div>
              <p className="terminal-label text-[#00FFD1] mb-1.5 flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-[#00FFD1] animate-pulse" />
                // Medical Fields
              </p>
              <h4 className="text-white font-semibold text-lg">One platform. Every specialty.</h4>
              <p className="text-white/55 text-sm mt-1">Explore XION&apos;s full range of clinical solutions.</p>
            </div>
            <Link
              to="/medical-fields"
              data-testid="footer-medical-fields-index"
              className="inline-flex items-center gap-1.5 self-start text-[#00FFD1] text-sm font-mono border border-[#00FFD1]/40 rounded-full px-4 py-2 hover:bg-[#00FFD1]/10 transition-colors whitespace-nowrap"
            >
              View all fields
              <ArrowRight size={14} />
            </Link>
          </div>
          <nav className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {MEDICAL_FIELDS.map(({ name, slug, Icon }) => (
              <Link
                key={slug}
                to={`/medical-fields/${slug}`}
                data-testid={`footer-medical-field-${slug}`}
                className="group flex items-center gap-2.5 rounded-lg border border-white/10 bg-white/[0.02] px-4 py-3 hover:border-[#00FFD1]/60 hover:bg-[#00FFD1]/[0.06] transition-colors"
              >
                <Icon size={22} className="text-[#00FFD1] shrink-0" />
                <span className="text-white/80 text-sm font-medium group-hover:text-[#00FFD1] transition-colors">{name}</span>
              </Link>
            ))}
          </nav>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-white/10 pt-6 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-white/40 text-xs font-mono">
            © {new Date().getFullYear()} XION GmbH. Philippines distribution by {distributor.shortName}.
          </p>
          <div className="flex items-center gap-5 text-xs text-white/40">
            <a
              href="https://www.facebook.com/fms.medequip/"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="FMSInc. on Facebook"
              data-testid="footer-facebook"
              className="text-white/60 hover:text-[#00FFD1] transition-colors"
            >
              <Facebook size={16} />
            </a>
            <Link to="/privacy-policy" data-testid="footer-privacy-link" className="font-mono hover:text-[#00FFD1] transition-colors">
              Privacy
            </Link>
            <span className="font-mono">v2.0.0</span>
            <span className="w-2 h-2 rounded-full bg-[#00FFD1] animate-pulse" title="System Online" />
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
